/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-column.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import { IPanelGraphCategory } from './column-components/panelGraphCategory.interface';
import RangeSelector from './column-components/range-selector';
import { useBoolean, useTranslation } from 'hooks';
import {
    PanelDisplayUnits,
    RowDataSourceTable,
    PanelOrderBy,
    PanelColumnType,
    PanelGraphCategoryClause,
    PanelColumnRangeCategory,
    PanelTimePeriod,
} from 'types/graphql-global-types';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import Constants from '@utils/constants';

const { displayUnits, columnOrders, categoryTypes, dataInclusionPeriods } = PanelConstants;

const Step2Column = forwardRef(
    (
        {
            panelState,
            setPanelState,
            setPage,
            page,
            updateState,
            show,
            updateErrorState,
            canSave,
            scheduleOptions,
        }: any,
        ref,
    ) => {
        const { t } = useTranslation();
        const initialState = {
            dataSource: panelState.dataSource.dataSource,
            xAxisDataField: panelState.dataSource.xAxisDataField,
            yAxisDataField: panelState.dataSource.yAxisDataField,
            sortField: panelState.dataSource.sortField,
            dataInclusionUnit: `${panelState.graph.dataInclusionUnit}`,
            dataInclusionPeriod: panelState.graph.dataInclusionPeriod,
            categoryRangeUnit: `${panelState.graph.categoryRangeUnit}`,
            categoryRangePeriod: panelState.graph.categoryRangePeriod,
            countField: panelState.dataSource.countField,
            rateByField: panelState.dataSource.rateByField,
            rateByType: panelState.dataSource.rateByType,
            timePeriod: panelState.dataSource.timePeriod,
            flagField: panelState.dataSource.flagField,
            countFirstEventTypeOnly:
                panelState.dataSource.countFirstEventTypeOnly && panelState.graph.columnType !== PanelColumnType.RANGED,
            displayUnits:
                panelState.graph.displayUnits === ''
                    ? panelState.graph.columnType === PanelColumnType.RANGED
                        ? PanelDisplayUnits.PERCENT_BY_POPULATION
                        : PanelDisplayUnits.PERCENT
                    : panelState.graph.displayUnits,
            hideStudyColumns: panelState.graph.hideStudyColumns,
            orderBy: panelState.graph.orderBy ?? PanelOrderBy.LABEL_ASCENDING,
            categories: panelState.graph.categories,
            columnRangeCategory: panelState.graph.columnRangeCategory,
            scheduleId: panelState.scheduleId,
        };
        const errorsState = {
            dataSource: '',
            xAxisDataField: '',
            yAxisDataField: '',
            sortField: '',
            dataInclusionUnit: '',
            dataInclusionPeriod: '',
            categoryRangeUnit: '',
            categoryRangePeriod: '',
            columnRangeCategory: '',
            countField: '',
            rateByField: '',
            rateByType: '',
            timePeriod: '',
            flagField: '',
            countFirstEventTypeOnly: '',
            displayUnits: '',
            hideStudyColumns: '',
            orderBy: '',
            categories: '',
            scheduleId: '',
        } as any;

        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...errorsState });
        const scheduleInactive = useBoolean(false);

        const dataInclusionUnits = Array.from(Array(30).keys()).map((item: number) => ({
            id: `${item + 1}`,
            text: `${item + 1}`,
        }));
        dataInclusionUnits.unshift({
            id: '',
            text: t('ra:selectValue'),
        });

        const dataInclusionPeriodsForColumnGraphs = dataInclusionPeriods.filter(
            (x: any) => x.id !== PanelTimePeriod.QUARTERS,
        );

        useEffect(() => {
            const scheduleOption = scheduleOptions.find((x: any) => x.id === state.scheduleId);
            scheduleInactive.setValue(scheduleOption?.id === state.scheduleId && scheduleOption?.active === false);
        }, [state.scheduleId, scheduleOptions]);

        useEffect(() => {
            if (panelState)
                setState({
                    dataSource: panelState.dataSource.dataSource,
                    xAxisDataField: panelState.dataSource.xAxisDataField,
                    yAxisDataField: panelState.dataSource.yAxisDataField,
                    sortField: panelState.dataSource.sortField,
                    countField: panelState.dataSource.countField,
                    rateByField: panelState.dataSource.rateByField,
                    rateByType: panelState.dataSource.rateByType,
                    timePeriod: panelState.dataSource.timePeriod,
                    flagField: panelState.dataSource.flagField,
                    countFirstEventTypeOnly:
                        panelState.dataSource.countFirstEventTypeOnly &&
                        panelState.graph.columnType !== PanelColumnType.RANGED,
                    displayUnits:
                        panelState.graph.displayUnits === ''
                            ? panelState.graph.columnType === PanelColumnType.RANGED
                                ? PanelDisplayUnits.PERCENT_BY_POPULATION
                                : PanelDisplayUnits.PERCENT
                            : panelState.graph.displayUnits,
                    hideStudyColumns: panelState.graph.hideStudyColumns,
                    orderBy: panelState.graph.orderBy ?? PanelOrderBy.LABEL_ASCENDING,
                    categories: panelState.graph.categories,
                    dataInclusionUnit:
                        panelState.graph.columnType === PanelColumnType.RANGED
                            ? panelState.graph.dataInclusionUnit
                                ? panelState.graph.dataInclusionUnit
                                : '1'
                            : panelState.graph.dataInclusionUnit,
                    dataInclusionPeriod:
                        panelState.graph.columnType === PanelColumnType.RANGED
                            ? panelState.graph.dataInclusionPeriod
                                ? panelState.graph.dataInclusionPeriod
                                : PanelTimePeriod.MONTHS
                            : panelState.graph.dataInclusionPeriod,
                    categoryRangePeriod: panelState.graph.categoryRangePeriod,
                    categoryRangeUnit: panelState.graph.categoryRangeUnit,
                    columnRangeCategory: panelState.graph.columnRangeCategory ?? 'NUMBER',
                    scheduleId: panelState.scheduleId,
                });
        }, [panelState]);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            let error = validateFields(value, field, length, required);
            if (field === 'orderBy') {
                error = { ...error, scheduleId: '' };
            }
            if (
                field === 'scheduleId' &&
                panelState.graph.columnType !== PanelColumnType.RANGED &&
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                value === Constants.ObjectId.Empty
            ) {
                error = { scheduleId: t('ra:requiredFieldError') };
            }
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: string) => {
            if (fieldName === 'columnRangeCategory') {
                setState({
                    ...state,
                    [fieldName]: value,
                    xAxisDataField: '',
                });
                updateState({ target: { name: key || fieldName, value } });
                updateState({ target: { name: 'dataSource.xAxisDataField', value: '' } });
            } else if (fieldName === 'dataSource') {
                setState({
                    ...state,
                    [fieldName]: value,
                    xAxisDataField: '',
                    sortField: '',
                });
                updateState({ target: { name: key || fieldName, value } });
                updateState({ target: { name: 'dataSource.sortField', value: '' } });
                updateState({ target: { name: 'dataSource.xAxisDataField', value: '' } });
            } else if (fieldName === 'displayUnits') {
                setState({
                    ...state,
                    [fieldName]: value,
                });
                updateState({ target: { name: key || fieldName, value } });
                if (value !== PanelDisplayUnits.PERCENT) {
                    setErrors({
                        ...errors,
                        sortField: null,
                    });
                    return;
                }
            } else {
                setState({
                    ...state,
                    [fieldName]: value,
                });
                updateState({ target: { name: key || fieldName, value } });
                if (fieldName === 'orderBy') {
                    updateState({ target: { name: 'scheduleId', value: '' } });
                }
            }

            validate(value, fieldName, length, required);
        };

        const onDisplayUnitChange = (fieldName: string, targetValue?: string) => {
            if (targetValue === PanelDisplayUnits.PERCENT_BY_POPULATION) {
                setState({
                    ...state,
                    countFirstEventTypeOnly: false,
                });
                updateState({ target: { name: 'dataSource.countFirstEventTypeOnly', value: false } });
            }
            onChange(fieldName, null, false, 'graph.displayUnits', targetValue);
        };

        const onCountFirstEventTypeChange = () => {
            setState({
                ...state,
                countFirstEventTypeOnly: !state.countFirstEventTypeOnly,
            });
            updateState({
                target: { name: 'dataSource.countFirstEventTypeOnly', value: !state.countFirstEventTypeOnly },
            });
        };

        const onHideStudyColumnsChange = () => {
            setState({
                ...state,
                hideStudyColumns: !state.hideStudyColumns,
            });
            updateState({ target: { name: 'graph.hideStudyColumns', value: !state.hideStudyColumns } });
        };

        const validationOnUpdate = () => {
            const requiredFields = ['dataSource', 'xAxisDataField', 'displayUnits'];
            const isDefaultScheduleId =
                panelState.graph.columnType !== PanelColumnType.RANGED &&
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                state.scheduleId === Constants.ObjectId.Empty;

            if (panelState.graph.columnType === PanelColumnType.RANGED) requiredFields.push('columnRangeCategory');

            if (panelState.graph.columnType !== PanelColumnType.STANDARD && state.columnRangeCategory === 'NUMBER')
                requiredFields.push('categories');

            if (panelState.graph.columnType !== PanelColumnType.STANDARD && state.columnRangeCategory === 'DATE')
                requiredFields.push(
                    'dataInclusionUnit',
                    'dataInclusionPeriod',
                    'categoryRangeUnit',
                    'categoryRangePeriod',
                );

            if (
                panelState.graph.columnType === PanelColumnType.STANDARD &&
                state.displayUnits === PanelDisplayUnits.PERCENT
            )
                requiredFields.push('sortField');

            if (
                panelState.graph.columnType === PanelColumnType.STANDARD &&
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER
            )
                requiredFields.push('scheduleId');

            const updateErrors = (errs: { [key: string]: string }[]) => {
                setErrors({
                    ...errs,
                    ...(isDefaultScheduleId ? { scheduleId: t('ra:requiredFieldError') } : {}),
                });
            };

            return (
                checkRequiredFields({ fields: requiredFields, state, setErrors: updateErrors, errors }) &&
                !isDefaultScheduleId
            );
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                setPanelState({
                    ...panelState,
                    graph: {
                        ...panelState.graph,
                        categories: state.categories,
                        categoryRangeUnit: state.categoryRangeUnit,
                        categoryRangePeriod: state.categoryRangePeriod,
                        columnRangeCategory: state.columnRangeCategory,
                        dataInclusionUnit: state.dataInclusionUnit,
                        dataInclusionPeriod: state.dataInclusionPeriod,
                        displayUnits: state.displayUnits,
                    },
                    dataSource: {
                        ...panelState.dataSource,
                        dataSource: state.dataSource,
                        xAxisDataField: state.xAxisDataField,
                        yAxisDataField: state.yAxisDataField,
                        sortField: state.sortField,
                        countField: state.countField,
                        rateByField: state.rateByField,
                        timePeriod: state.timePeriod,
                        flagField: state.flagField,
                        countFirstEventTypeOnly: state.countFirstEventTypeOnly,
                    },
                    modelParams: {
                        ...panelState.modelParams,
                    },
                });
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...errorsState });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt mb-30">
                    {panelState.graph.columnType === PanelColumnType.RANGED && (
                        <FormControl variant="outlined" className="form-control-type not-full mr">
                            <SelectWithCategories
                                label={t('admin:categoryType')}
                                dataArray={categoryTypes}
                                onChange={() => (event: any) => {
                                    onChange(
                                        'columnRangeCategory',
                                        null,
                                        false,
                                        'graph.columnRangeCategory',
                                        event.target?.value,
                                    );
                                }}
                                fieldSelect="columnRangeCategory"
                                currentState={state.columnRangeCategory}
                                labelField="columnRangeCategory"
                                showWarning={false}
                                filterable={false}
                                customLabelId="columnRangeCategory-column-label-id"
                                customId="columnRangeCategory-id"
                                customClassName="edit-atts-column-category"
                                error={errors.columnRangeCategory}
                                disabled={!canSave}
                            />
                        </FormControl>
                    )}
                    <FormControl
                        variant="outlined"
                        className={`form-control-type not-full 
                            ${panelState.graph.columnType !== PanelColumnType.RANGED ? 'mr' : ''}`}
                    >
                        <DataSourceSelector
                            label={t('admin:rowData')}
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="dataSource"
                            showDefault={false}
                            error={errors.dataSource}
                            customClassName="edit-atts-category"
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    {panelState.graph.columnType !== PanelColumnType.RANGED && (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <FieldSelector
                                label={t('admin:xAxisCategory')}
                                dataSource={state.dataSource}
                                formats={[Formats.Text, Formats.Int, Formats.Bool]}
                                value={state.xAxisDataField}
                                labelField="xAxisDataField"
                                fieldSelect="xAxisDataField"
                                onChange={(value: string) => {
                                    onChange('xAxisDataField', null, false, 'dataSource.xAxisDataField', value);
                                }}
                                customId="x-axis-category-column-id"
                                error={errors.xAxisDataField}
                                searchable
                                disabled={!canSave}
                            />
                        </FormControl>
                    )}
                </div>
                {panelState.graph.columnType !== PanelColumnType.RANGED ? (
                    <div className="type-edc-wrapper align-center mb-30">
                        <FormControl variant="outlined" className="form-control-type not-full mr">
                            <SelectWithCategories
                                label={t('admin:columnOrder')}
                                dataArray={columnOrders}
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(fieldName, null, false, 'graph.orderBy', event.target?.value);
                                }}
                                fieldSelect="orderBy"
                                currentState={state.orderBy}
                                labelField="orderBy"
                                showWarning={false}
                                filterable={false}
                                customLabelId="orderby-column-label-id"
                                customId="orderby-id"
                                customClassName="edit-atts-column-category"
                                error={errors.orderBy}
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <div className="checkboxes-wrapper">
                                <div className="right">
                                    <Checkbox
                                        rounded={null}
                                        id="hide-study-columns-checkbox"
                                        className="cell-checkbox no-border"
                                        checked={state.hideStudyColumns}
                                        onChange={onHideStudyColumnsChange}
                                        label={t('admin:hideStudyColumns')}
                                        data-testid="hide-study-columns-checkbox"
                                        size="medium"
                                    />
                                </div>
                            </div>
                        </FormControl>
                    </div>
                ) : null}
                {panelState.graph.columnType !== PanelColumnType.RANGED &&
                    state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER && (
                        <div className="type-edc-wrapper align-center mb-30">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <SelectWithCategories
                                    label={t('admin:scheduleNameR')}
                                    dataArray={scheduleOptions}
                                    onChange={() => (event: any) => {
                                        onChange('scheduleId', null, true, 'scheduleId', event.target?.value);
                                    }}
                                    fieldSelect="scheduleId"
                                    currentState={state.scheduleId}
                                    labelField="schedule"
                                    showWarning={scheduleInactive.value}
                                    customLabelId="schedule-id"
                                    customId="schedule-id"
                                    error={errors.scheduleId}
                                    customClassName={`${
                                        scheduleInactive.value ? 'form-action-type inactive' : 'form-action-type'
                                    }`}
                                />
                            </FormControl>
                        </div>
                    )}
                <div className="type-edc-wrapper">
                    {panelState.graph.columnType === PanelColumnType.RANGED && (
                        <FormControl variant="outlined" className="form-control-type not-full mr">
                            <FieldSelector
                                label={t('admin:xAxisCategory')}
                                dataSource={state.dataSource}
                                formats={
                                    panelState.graph.columnRangeCategory !== PanelColumnRangeCategory.DATE
                                        ? [Formats.Int, Formats.Float]
                                        : [Formats.Date, Formats.PartialDate]
                                }
                                value={state.xAxisDataField}
                                labelField="xAxisDataField"
                                fieldSelect="xAxisDataField"
                                onChange={(value: string) => {
                                    onChange('xAxisDataField', null, false, 'dataSource.xAxisDataField', value);
                                }}
                                customId="x-axis-category-column-id"
                                error={errors.xAxisDataField}
                                searchable
                                disabled={!canSave}
                            />
                        </FormControl>
                    )}
                    <FormControl
                        variant="outlined"
                        className={`form-control-type not-full 
                            ${panelState.graph.columnType !== PanelColumnType.RANGED ? 'mr' : ''}`}
                    >
                        <SelectWithCategories
                            label={t('admin:displayUnits')}
                            dataArray={
                                // Filter out the PERCENT (% by Calculation) for ranged columns
                                panelState.graph.columnType === PanelColumnType.RANGED
                                    ? displayUnits.filter((unit: any) => unit.id !== PanelDisplayUnits.PERCENT)
                                    : displayUnits
                            }
                            onChange={(fieldName?: string) => (event: any) => {
                                onDisplayUnitChange(fieldName, event.target?.value);
                            }}
                            fieldSelect="displayUnits"
                            currentState={state.displayUnits}
                            labelField="displayUnits"
                            showWarning={false}
                            filterable={false}
                            customLabelId="display-units-column-label-id"
                            customId="display-units-id"
                            customClassName="edit-atts-column-category"
                            error={errors.displayUnits}
                            disabled={!canSave}
                        />
                    </FormControl>
                    {panelState.graph.columnType !== PanelColumnType.RANGED &&
                        state.displayUnits === PanelDisplayUnits.PERCENT && (
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <FieldSelector
                                    label={t('admin:calculatedBy')}
                                    dataSource={RowDataSourceTable.SITE}
                                    formats={[Formats.Int, Formats.Float]}
                                    value={state.sortField}
                                    onChange={(value: string) => {
                                        onChange('sortField', null, false, 'dataSource.sortField', value);
                                    }}
                                    labelField="sortField"
                                    fieldSelect="sortField"
                                    customId="calculated-by-column-id"
                                    error={errors.sortField}
                                    searchable
                                    disabled={!canSave}
                                />
                            </FormControl>
                        )}
                </div>
                {panelState.graph.columnType !== PanelColumnType.RANGED &&
                    state.displayUnits !== PanelDisplayUnits.PERCENT_BY_POPULATION && (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <div className="checkboxes-wrapper">
                                    <div className="left-part">
                                        <Checkbox
                                            rounded={null}
                                            id="count-first-event-checkbox"
                                            className="cell-checkbox no-border"
                                            checked={state.countFirstEventTypeOnly}
                                            onChange={onCountFirstEventTypeChange}
                                            label={t('admin:countFirstEventTypeOnly')}
                                            data-testid="count-first-event-checkbox"
                                            size="medium"
                                        />
                                    </div>
                                </div>
                            </FormControl>
                        </div>
                    )}
                {panelState.graph.columnType === PanelColumnType.RANGED && state.columnRangeCategory === 'NUMBER' ? (
                    <>
                        <div>
                            <FormControl variant="outlined" className="form-control-type full mr">
                                <div className="checkboxes-wrapper">
                                    <div className="right">
                                        <Checkbox
                                            rounded={null}
                                            id="hide-study-columns-checkbox"
                                            className="cell-checkbox no-border"
                                            checked={state.hideStudyColumns}
                                            onChange={onHideStudyColumnsChange}
                                            label={t('admin:hideStudyColumns')}
                                            data-testid="hide-study-columns-checkbox"
                                            size="medium"
                                        />
                                    </div>
                                </div>
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl className="form-control-type full">
                                <RangeSelector
                                    data={state.categories}
                                    onChangeCategory={(data: IPanelGraphCategory[]) => {
                                        const categories = data.map((item: IPanelGraphCategory) => ({
                                            index: item.index,
                                            min: +item.min,
                                            max: item.max !== null || (item.max as string).length ? +item.max : null,
                                            clause:
                                                item.clause && item.clause.length
                                                    ? item.clause
                                                    : PanelGraphCategoryClause.NONE,
                                        }));
                                        setState({
                                            ...state,
                                            categories,
                                        });
                                        updateState({ target: { name: 'graph.categories', value: categories } });
                                        setErrors({
                                            ...errors,
                                            categories: '',
                                        });
                                    }}
                                />
                                {errors.categories && <p className="error-message">{errors.categories}</p>}
                            </FormControl>
                        </div>
                    </>
                ) : panelState.graph.columnType === PanelColumnType.RANGED && state.columnRangeCategory === 'DATE' ? (
                    <>
                        <FormControl variant="outlined" className="form-control-type full mr">
                            <div className="checkboxes-wrapper">
                                <div className="right">
                                    <Checkbox
                                        rounded={null}
                                        id="hide-study-columns-checkbox"
                                        className="cell-checkbox no-border"
                                        checked={state.hideStudyColumns}
                                        onChange={onHideStudyColumnsChange}
                                        label={t('admin:hideStudyColumns')}
                                        data-testid="hide-study-columns-checkbox"
                                        size="medium"
                                    />
                                </div>
                            </div>
                        </FormControl>
                        <OutlinedWrapper customClass="mb" label={t('admin:dataInclusionPeriod')}>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <SelectWithCategories
                                        customClassName="edit-control-review-frequency-units"
                                        showWarning={false}
                                        fieldSelect="dataInclusionUnit"
                                        onChange={(fieldName?: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.dataInclusionUnit',
                                                event.target?.value,
                                            );
                                        }}
                                        label={t('admin:unit')}
                                        filterable={false}
                                        labelField="dataInclusionUnit"
                                        dataArray={dataInclusionUnits}
                                        customId="data-inclusion-unit"
                                        currentState={state.dataInclusionUnit?.toString()}
                                        error={errors.dataInclusionUnit}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <SelectWithCategories
                                        customClassName="edit-control-review-frequency-units"
                                        showWarning={false}
                                        fieldSelect="dataInclusionPeriod"
                                        onChange={(fieldName?: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.dataInclusionPeriod',
                                                event.target?.value,
                                            );
                                        }}
                                        label={t('admin:period')}
                                        filterable={false}
                                        labelField="dataInclusionPeriod"
                                        dataArray={dataInclusionPeriodsForColumnGraphs}
                                        customId="data-inclusion-period"
                                        currentState={state.dataInclusionPeriod}
                                        error={errors.dataInclusionPeriod}
                                    />
                                </FormControl>
                            </div>
                        </OutlinedWrapper>
                        <OutlinedWrapper customClass="mb" label={t('admin:categoryRanges')}>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <SelectWithCategories
                                        customClassName="edit-control-review-frequency-units"
                                        showWarning={false}
                                        fieldSelect="categoryRangeUnit"
                                        onChange={(fieldName?: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.categoryRangeUnit',
                                                event.target?.value,
                                            );
                                        }}
                                        label={t('admin:unit')}
                                        filterable={false}
                                        labelField="categoryRangeUnit"
                                        dataArray={dataInclusionUnits}
                                        customId="category-inclusion-unit"
                                        currentState={state.categoryRangeUnit.toString()}
                                        error={errors.categoryRangeUnit}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <SelectWithCategories
                                        customClassName="edit-control-review-frequency-units"
                                        showWarning={false}
                                        fieldSelect="categoryRangePeriod"
                                        onChange={(fieldName?: string) => (event: any) => {
                                            onChange(
                                                fieldName,
                                                null,
                                                false,
                                                'graph.categoryRangePeriod',
                                                event.target?.value,
                                            );
                                        }}
                                        label={t('admin:period')}
                                        filterable={false}
                                        labelField="categoryRangePeriod"
                                        dataArray={dataInclusionPeriodsForColumnGraphs}
                                        customId="category-inclusion-period"
                                        currentState={state.categoryRangePeriod}
                                        error={errors.categoryRangePeriod}
                                    />
                                </FormControl>
                            </div>
                        </OutlinedWrapper>
                    </>
                ) : null}
            </div>
        );
    },
);

export default Step2Column;
